import type { Product } from "@/contexts/product-context"
import { getLoginSession } from "@/lib/login-session"
import type { ProductHomeCard } from "@/lib/product-home"
import { productVisibleInActiveRole } from "@/lib/workspace-rbac"

export const STUDENT_EXXAT_ONE_PRODUCT = "exxat-one-schools" as const satisfies Product

/**
 * Student home row copy from exxat.com/products/exxat-one (For Students) and
 * Jobs on Exxat One for learners (one.exxat.com/learners).
 */
export const STUDENT_EXXAT_ONE_HOME_TAGLINE =
  "Track placements and onboarding in one place. Stay on top of compliance for each site, and explore Jobs on Exxat One matched to your clinical training."

/** Active sign-in is the student role (not faculty acting as student via Open as). */
export function isStudentHomeSession(): boolean {
  return getLoginSession().role === "student"
}

/**
 * Teaser row for students who only open Clin Ed. When the session grants Exxat
 * One explicitly (multi-app student flows), it stays a normal owned app door.
 */
export function usesStudentExxatOneIncludedRow(): boolean {
  if (!isStudentHomeSession()) return false
  return !productVisibleInActiveRole(STUDENT_EXXAT_ONE_PRODUCT)
}

/** Student-facing copy for Exxat One on the products home. */
export function studentExxatOneTagline(_card?: ProductHomeCard): string {
  return STUDENT_EXXAT_ONE_HOME_TAGLINE
}

/**
 * Exxat One card for the student included row. Pass unfiltered `inventory.owned`
 * from {@link buildProductHomeInventory} so entitled apps stay reachable when
 * persona filtering removes them from the visible owned list.
 */
export function studentExxatOneHomeCard(
  inventoryOwned: ProductHomeCard[],
  available: ProductHomeCard[],
): ProductHomeCard | null {
  if (!usesStudentExxatOneIncludedRow()) return null

  const base =
    inventoryOwned.find(card => card.product === STUDENT_EXXAT_ONE_PRODUCT) ??
    available.find(card => card.product === STUDENT_EXXAT_ONE_PRODUCT)

  if (!base) return null

  return {
    ...base,
    tagline: studentExxatOneTagline(base),
  }
}

/** Keep Exxat One off other lists only while it uses the student included row. */
export function omitStudentExxatOneFromAppLists<T extends { product: Product }>(
  cards: readonly T[],
): T[] {
  if (!usesStudentExxatOneIncludedRow()) return [...cards]
  return cards.filter(card => card.product !== STUDENT_EXXAT_ONE_PRODUCT)
}
